import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { useQuery, useMutation } from "convex/react";
import { useConvexAuth } from "@convex-dev/auth/react";
import { api } from "@todotogether/backend/_generated/api";
import {
  errorMessage,
  GENERIC_ERROR_MESSAGE,
  inviteDeepLinkUrl,
  joinPreviewCopy,
  inviteRoleDetail,
} from "@todotogether/shared";

/**
 * The Invite Link landing page (ticket #14): what someone sees when they
 * open a share link. Signed-out visitors sign up first and are bounced
 * back here; signed-in users join in one click (quota + role + colour are
 * all enforced server-side by invites:join — this page only displays).
 * The role line states the visitor's own truth (#401): an active member
 * opening a higher link sees the upgrade spelled out before they tap
 * Join; anyone the link cannot lift reads as already-a-member.
 *
 * Brand treatment (#252): the page dresses in the stationery language —
 * desk background, sheet card, the clipboard mark, the list name in the
 * reading serif — and the "Open in the app" handoff is a first-class
 * button: on phone-width viewports it is the primary CTA (an installer's
 * best journey), on desktop the web flow leads and the handoff demotes to
 * a visible outline button. Both paths always render; the media query
 * only swaps prominence and order, never hides a journey.
 */

/** Sheet card shared by the valid and invalid states (brief §1: hairline,
 *  soft small shadow, page radius). Inline brand hexes until #122 ports
 *  the token table to web. */
const sheetCard =
  "w-full max-w-md rounded-[18px] border border-line bg-page p-8 text-center shadow-[0_5px_16px_rgba(12,22,36,0.07)]";

export default function InvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useConvexAuth();
  const preview = useQuery(
    api.invites.preview,
    token ? { token } : "skip",
  );
  const join = useMutation(api.invites.join);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  if (isLoading || preview === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-muted" data-testid="invite-loading">
          Loading…
        </p>
      </main>
    );
  }

  if (preview === null || !preview.valid) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-paper px-6">
        {/* Brand mark (ticket #120): static from public/, cut from
            docs/design/brand/mark.svg — shown above the sheet the way the
            auth screens show it above their forms. */}
        <img
          src="/mark.svg"
          alt="ToDoTogether"
          width={56}
          height={56}
          className="block"
        />
        <div className={sheetCard}>
          <h1 className="text-xl font-bold text-ink">
            This invite link isn't valid
          </h1>
          <p className="mt-2 text-sm text-muted" data-testid="invite-invalid">
            It may have been regenerated by the list's owner, or the list has
            been deleted.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-blue-fill px-4 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
          >
            Go to ToDoTogether
          </Link>
        </div>
      </main>
    );
  }

  async function doJoin() {
    setError(null);
    setJoining(true);
    try {
      const result = await join({ token });
      navigate(`/app/list/${result.listId}`);
    } catch (err) {
      setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
      setJoining(false);
    }
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-paper px-6">
      {/* Brand mark (ticket #120): static from public/, cut from
          docs/design/brand/mark.svg — shown above the sheet the way the
          auth screens show it above their forms, so the invitation and
          the sign-up it leads to read as one product. */}
      <img
        src="/mark.svg"
        alt="ToDoTogether"
        width={56}
        height={56}
        className="block"
      />
      <div className={sheetCard}>
        <p className="text-sm text-muted" data-testid="invite-inviter">
          {preview.inviterName
            ? `${preview.inviterName} invited you to join`
            : "You've been invited to join"}
        </p>
        <h1
          className="mt-1 font-serif text-[28px] font-bold leading-[34px] text-ink"
          data-testid="invite-list-name"
        >
          {preview.listName}
        </h1>
        <p className="mt-2 text-sm text-muted" data-testid="invite-role">
          {joinPreviewCopy(preview.callerRole, preview.role) ?? (
            <>
              You'll join as{" "}
              <span className="font-semibold capitalize">{preview.role}</span>
              {inviteRoleDetail(preview.role)}
              .
            </>
          )}
        </p>

        {/* Action column (#252): order swaps by viewport — desktop leads
            with the web flow and keeps the app handoff as a visible
            outline secondary; phone width leads with the handoff (the
            filled accent is the page's one loud element) and demotes the
            web flow below it. min-h-[44px] + gap-2 keep every target
            touch-sized. The serif name above is the document voice;
            these controls stay chrome sans. */}
        <div className="mt-6 flex flex-col gap-2">
          {!isAuthenticated ? (
            <Link
              to={`/signup?invite=${encodeURIComponent(token)}`}
              className="order-1 flex min-h-[44px] items-center justify-center rounded-lg bg-blue-fill px-4 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue max-md:order-2 max-md:border max-md:border-line max-md:bg-page max-md:text-ink max-md:shadow-none max-md:hover:bg-pressed"
              data-testid="invite-signup"
            >
              Sign up to join
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => void doJoin()}
              disabled={joining}
              className="order-1 flex min-h-[44px] items-center justify-center rounded-lg bg-blue-fill px-4 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue max-md:order-2 max-md:border max-md:border-line max-md:bg-page max-md:text-ink max-md:shadow-none max-md:hover:bg-pressed"
              data-testid="invite-join"
            >
              {joining ? "Joining…" : "Join list"}
            </button>
          )}

          {/* Mobile handoff (ticket #22): an installer can hop straight into
              the app's Join flow; the web flow stays the fallback for
              non-installers, so the shareable link itself never changes.
              Promoted to a real button on phone widths (#252). */}
          <a
            href={inviteDeepLinkUrl(token)}
            className="order-2 flex min-h-[44px] items-center justify-center rounded-lg border border-blue px-4 py-2 text-sm font-medium text-blue no-underline hover:bg-wash focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue max-md:order-1 max-md:border-transparent max-md:bg-blue-fill max-md:text-on-blue max-md:hover:bg-blue-fill-pressed"
            data-testid="open-in-app"
          >
            Open in the app
          </a>

          {!isAuthenticated && (
            <Link
              to={`/signin?invite=${encodeURIComponent(token)}`}
              className="order-3 flex min-h-[44px] items-center justify-center rounded-lg px-4 py-2 text-sm text-muted underline hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
              data-testid="invite-signin"
            >
              Already have an account? Sign in
            </Link>
          )}
        </div>

        {error && (
          <p
            className="mt-4 rounded-lg border border-danger/40 bg-page px-3 py-2 text-sm text-danger"
            data-testid="invite-error"
          >
            {error}
          </p>
        )}
      </div>
    </main>
  );
}
